import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { PlusLg } from 'react-bootstrap-icons';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { Pagination } from '../../shared/components/Pagination';
import { ERole } from '../../shared/enums/ERole';
import { handleError } from '../../shared/functions/handleError';
import { usePagination } from '../../shared/hooks/usePagination';
import { ITruckBrand } from '../../shared/interfaces/ITruck';
import { IUser } from '../../shared/interfaces/IUser';
import { RootState } from '../../store/store';
import TruckBrandForm from './components/TruckBrandForm';
import TruckBrandTable from './components/TruckBrandTable';
import { AdminTruckBrandService } from './service';

const schema = yup.object().shape({
  firstServiceKm: yup
    .number()
    .typeError('First service km must be a number')
    .required('First service km is required')
    .min(0, 'First service km must be a positive number'),

  majorServiceIntervalKm: yup
    .number()
    .typeError('Major service interval km must be a number')
    .required('Major service interval km is required')
    .min(1, 'Major service interval must be at least 1 km'),

  minorServiceIntervalKm: yup
    .number()
    .typeError('Minor service interval km must be a number')
    .required('Minor service interval km is required')
    .min(1, 'Minor service interval must be at least 1 km'),

  name: yup
    .string()
    .required('Name is required')
    .min(1, 'Name cannot be empty'),
});

const AdminTruckBrandPage = () => {
  const { t } = useTranslation();
  const { role } = useSelector(
    (state: RootState) => state.auth.userData,
  ) as IUser;
  const isAdmin = role === ERole.ADMIN;
  const [isActionModalOpened, setIsActionModalOpened] = useState(false);

  const {
    register,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ITruckBrand>({
    resolver: yupResolver(schema),
  });

  const {
    pagination,
    setTotalPages,
    setCount,
    changeFilterHandler,
    totalPages,
    page,
    handlePages,
    handleUpdateList,
  } = usePagination({
    perPage: 10,
  });
  const [truckBrands, setTruckBrands] = useState<ITruckBrand[]>([]);

  const handleCloseActionModal = () => {
    setIsActionModalOpened(false);
    reset({
      name: '',
    });
  };
  const handleSubmitActionModal = async (data: ITruckBrand) => {
    const isUpdate = !!data?.id;
    await AdminTruckBrandService[isUpdate ? 'update' : 'create'](data)
      .then((response) => {
        if (response) {
          handleUpdateList();
          handleCloseActionModal();
          toast.success(
            t(`toast.${isUpdate ? 'successUpdated' : 'successCreated'}`, {
              name: response?.name,
            }),
          );
        }
      })
      .catch(handleError);
  };
  useEffect(() => {
    AdminTruckBrandService.getAll(pagination)
      .then((response) => {
        setTruckBrands(response?.data);
        setTotalPages(Math.ceil(response?.count / response?.perPage));
      })
      .catch(handleError);
  }, [pagination, setCount, setTotalPages]);
  return (
    <div>
      <div className="mb-3">
        <div className="w-100 d-flex justify-content-between align-items-center">
          {isAdmin && (
            <Button
              variant={'success'}
              className={'d-flex align-items-center'}
              onClick={() => setIsActionModalOpened(true)}
            >
              <PlusLg className={'me-1'} />
              {t('button.create')}
            </Button>
          )}
          <Form.Control
            type="email"
            className={'w-25'}
            onChange={(e) => changeFilterHandler('name', e.target.value)}
            placeholder={t('general.searchByName')}
          />
        </div>
      </div>
      <TruckBrandTable
        truckBrands={truckBrands}
        isAdmin={isAdmin}
        reset={reset}
        setIsActionModalOpened={setIsActionModalOpened}
        handleUpdateList={handleUpdateList}
      />

      <Pagination
        page={page}
        totalPages={totalPages}
        handlePagination={handlePages}
        changeFilterHandler={changeFilterHandler}
      />
      <Modal
        show={isActionModalOpened}
        onHide={handleCloseActionModal}
        size={'lg'}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t(watch('id') ? 'general.update' : 'general.create')}{' '}
            {t('general.truckBrand')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <TruckBrandForm register={register} errors={errors} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseActionModal}>
            {t('button.close')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(handleSubmitActionModal)}
          >
            {t(watch('id') ? 'button.update' : 'button.create')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminTruckBrandPage;
